import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IAbstractFilterForm } from '@app/pages/dashboard/interfaces/abstract-filter-form';
import { combineLatest, Observable, Subject } from 'rxjs';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ConfigurableFilterBase } from './au-configurable-filter-base.class';
import {
	AuFilterNameType,
	AuFiltersGlobalConfig,
	IAuFiltersConfig,
} from '@app/configs/filters/au-configurable-filters.config';
import {
	AuFilterOptionsListName,
	SelectableOptionsService,
} from '@shared/ui-components/au-configurable-filter/selectable-options.service';
import { IConfigurableMultiselectData } from '@shared/ui-components/au-configurable-filter/interfaces/au-configurable-filter.interfaces';
import {
	getPatchDataObject,
	getResetDataObject,
} from '@shared/ui-components/au-configurable-filter/au-configurable-filter-functions';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { AuSelectCheckAllModule } from '../../au-select-check-all/au-select-check-all.module';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'au-configurable-filter',
	templateUrl: './au-configurable-filter.component.html',
	styleUrls: ['./au-configurable-filter.component.scss'],
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		ReactiveFormsModule,
		MatInputModule,
		MatIconModule,
		MatRippleModule,
		AuSelectCheckAllModule,
		NgxTolgeeModule,
	],
})
export class AuConfigurableFilterComponent
	extends ConfigurableFilterBase
	implements OnInit, OnDestroy, IAbstractFilterForm
{
	@Input() set filterName(name: AuFilterNameType) {
		this.config = AuFiltersGlobalConfig[name];
	}

	@Input() isFilterInMobileMode = false;

	config: IAuFiltersConfig;

	private destroy$ = new Subject<void>();

	constructor(
		protected fb: UntypedFormBuilder,
		private store$: Store<any>,
		private selectableOptionsService: SelectableOptionsService
	) {
		super(fb);
	}

	ngOnInit() {
		this.initForm();

		const filtersState$ = this.store$
			.select(this.config.filterStateSelector$)
			.pipe(take(1));

		combineLatest([
			filtersState$,
			this.getOptions(this.firstOptionsName),
			this.getOptions(this.secondOptionsName),
		])
			.pipe(take(3))
			.subscribe(value => {
				const filtersState = value[0];
				this.firstSelectEntitiesList = this.addToEntitiesListNullValue(
					value[1],
					this.firstSelectConfig
				);
				this.secondSelectEntitiesList = this.addToEntitiesListNullValue(
					value[2],
					this.secondSelectConfig
				);

				this.setDefaultFilterValues();

				this.formGroupFilters.patchValue(
					getPatchDataObject(
						this.config.fieldsConfig,
						filtersState,
						this.firstSelectDefaultValue,
						this.secondSelectDefaultValue
					),
					{ emitEvent: false }
				);
			});

		this.formGroupFilters.valueChanges
			.pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged())
			.subscribe(value => this.onFilterChange(value));
	}

	setDefaultFilterValues() {
		this.firstSelectDefaultValue = this.setDefaultValueForSelect(
			this.firstSelectConfig,
			this.firstSelectEntitiesList
		);

		this.secondSelectDefaultValue = this.setDefaultValueForSelect(
			this.secondSelectConfig,
			this.secondSelectEntitiesList
		);
	}

	setDefaultValueForSelect(
		selectConfig: IConfigurableMultiselectData,
		entitiesList: { id: string; name: string }[]
	): string | string[] {
		if (selectConfig.disableMultipleSelection) {
			if (selectConfig.defaultValue) {
				return selectConfig.defaultValue;
			} else {
				throw new Error(
					`If you use 'disableMultipleSelection' property, you need to provide a default value for ${selectConfig.formControlName}`
				);
			}
		} else {
			return entitiesList.map(item => item.id);
		}
	}

	onFilterChange(value: { [key: string]: any }): void {
		if (!this.isFilterInMobileMode) {
			this.store$.dispatch(this.config.changeFilterToStoreAction(value));
		}
	}

	filterReset() {
		this.formGroupFilters.setValue(
			getResetDataObject(
				this.config.fieldsConfig,
				this.firstSelectDefaultValue,
				this.secondSelectDefaultValue
			),
			{ emitEvent: false }
		);
	}

	filterSubmit(): void {
		this.store$.dispatch(
			this.config.changeFilterToStoreAction(this.formGroupFilters.value)
		);
	}

	getOptions(optionsName: AuFilterOptionsListName): Observable<any> {
		return this.selectableOptionsService.getSelectableOptions(optionsName);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
